import React, { Component } from 'react';
import { transaction } from 'mobx';
import connectorStyles from './element-types/connector-element/index.css';
import { pointInRect } from '../../../../../../../slide/infrastructure/module/independent/graphic/index';
import Cursor from '../../../../../../../css/ppt-editor-cursor-global.less';
import set from 'lodash/set';
import type EditingStateManager from '@src/slide/application/service/editing-state-manager';
import { IElementModelStyle } from '@src/slide/domain/ui-model/element-modeles/Interface/model-props/element-model-props.interface';
import { IElementBaseModel } from '@src/slide/domain/ui-model/element-modeles/Interface/base/element-base-model.interface';
import { IBaseGraphics } from '@tencent/alloy-graphics';

export interface ConnectedProps {
    styleModel: IElementModelStyle;
    component: IElementBaseModel;
    scale: number;
    pathParser: IBaseGraphics;
    editingStateManager: EditingStateManager;
    needAdsorbTip?: boolean;
}

export interface ConnectedState {
    isOnElement: boolean;
}

class Connected<P extends ConnectedProps, S extends ConnectedState> extends Component<P, S> {
    public static displayName = 'Connected';

    protected pointRefs: (HTMLDivElement | null)[] = [];

    public constructor(props: P) {
        super(props);
        // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
        this.state = {
            isOnElement: false,
        } as S;
    }

    public render() {
        return (
            <React.Fragment>
                {
                    <div
                        onMouseMove={this.handleMouseMoveElement}
                        onTouchMove={this.handleMouseMoveElement}
                        onMouseOut={this.handleMouseOutElement}
                        className={'connecting-element-cover'}
                        style={{
                            position: 'absolute',
                            width: this.props.styleModel.width * this.getScaleNum(),
                            height: this.props.styleModel.height * this.getScaleNum(),
                            top: 0,
                            left: 0,
                            zIndex: 1000,
                            pointerEvents: 'auto',
                        }}
                    >
                    </div>
                }
                {
                    this.state.isOnElement && this.renderConnectedPoints()
                }
            </React.Fragment>
        );
    }

    protected handleMouseMoveElement = (e: any) => {
        if (!this.props.editingStateManager.isConnecting) {
            return;
        }
        if (this.state.isOnElement === false) {
            this.setState({
                isOnElement: true,
            });
        }
        const p = {
            x: e.clientX || e.changedTouches?.[0]?.clientX,
            y: e.clientY || e.changedTouches?.[0]?.clientY,
        };
        let index = -1;
        for (let i = 0; i < this.pointRefs.length; i++) {
            const currentRef = this.pointRefs[i];
            if (!currentRef) break;
            const boundingRect = currentRef.getBoundingClientRect();
            const rect = {
                left: boundingRect.left + (boundingRect.width / 2) -  ((boundingRect.width / 2) * 1.5),
                right: boundingRect.right - (boundingRect.width / 2) +  ((boundingRect.width / 2) * 1.5),
                top: boundingRect.top + (boundingRect.height / 2) -  ((boundingRect.height / 2) * 1.5),
                bottom: boundingRect.bottom - (boundingRect.height / 2) +  ((boundingRect.height / 2) * 1.5),
            };
            const isInRect = pointInRect(p, rect);
            if (isInRect) {
                index = i;
                break;
            }
        }
        if (this.props.needAdsorbTip) {
            this.handleTips(index);
        }
        const connectorInfo = this.props.editingStateManager.connectingInfo;
        if (!connectorInfo) {
            return;
        }
        transaction(() => {
            connectorInfo.isOnElement = true;
            if (connectorInfo.endIndex === 0) {
                if (index !== -1) {
                    set(connectorInfo.model, 'props.style.connectorInfo.start.id', this.props.component.id);
                    set(connectorInfo.model, 'props.style.connectorInfo.start.index', index);
                } else {
                    set(connectorInfo.model, 'props.style.connectorInfo.start', null);
                }
            } else if (connectorInfo.endIndex === 1) {
                if (index !== -1) {
                    set(connectorInfo.model, 'props.style.connectorInfo.end.id', this.props.component.id);
                    set(connectorInfo.model, 'props.style.connectorInfo.end.index', index);
                } else {
                    set(connectorInfo.model, 'props.style.connectorInfo.end', null);
                }
            }
        });
    };

    protected handleMouseOutElement = () => {
        if (this.state.isOnElement === true) {
            this.setState({
                isOnElement: false,
            });
        } else {
            return;
        }
        if (!this.props.editingStateManager.isConnecting) {
            return;
        }
        const connectorInfo = this.props.editingStateManager.connectingInfo;
        if (!connectorInfo) {
            return;
        }
        transaction(() => {
            connectorInfo.isOnElement = false;
            if (connectorInfo.endIndex === 0) {
                set(connectorInfo.model, 'props.style.connectorInfo.start', null);
            } else if (connectorInfo.endIndex === 1) {
                set(connectorInfo.model, 'props.style.connectorInfo.end', null);
            }
        });
    };

    protected renderConnectedPoints() {
        const points = this.getPoints();
        return points.map(this.renderConnectedPoint);
    }

    protected getScaleNum() {
        return this.props.scale ? this.props.scale : 1;
    }

    protected renderConnectedPoint = (point: any, index: number) => {
        const scale = this.getScaleNum();
        return (
            <div
                key={index}
                className={`${connectorStyles.connectedPoint} ${Cursor.move}`}
                style={{
                    left: (point.x * scale) - 5,
                    top: (point.y * scale) - 5,
                    position: 'absolute',
                    pointerEvents: 'auto',
                    boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.32)',
                }}
                ref={(el) => {
                    this.pointRefs[index] = el;
                }}
            >
            </div>
        );
    };

    private handleTips = (index: number) => {
        if (index !== -1) {
            this.addTipsCircle(index);
        } else {
            this.removeTipsCircle();
        }
    };

    private addTipsCircle = (index: number) => {
        const point = this.pointRefs[index];
        if (point?.childNodes.length === 0) {
            const tipsCircleDom = document.createElement('div');
            tipsCircleDom.style.backgroundColor = '#000';
            tipsCircleDom.style.width = '90px';
            tipsCircleDom.style.height = '90px';
            tipsCircleDom.style.borderRadius = '50%';
            tipsCircleDom.style.opacity = '0.2';
            tipsCircleDom.style.transform = 'translate(-50%, -50%)';
            tipsCircleDom.style.position = 'absolute';
            tipsCircleDom.style.left = '50%';
            tipsCircleDom.style.top = '50%';
            point?.appendChild(tipsCircleDom);
        }
    };

    private removeTipsCircle = () => {
        this.pointRefs.forEach((ele) => {
            if (ele?.children?.[0]) {
                ele.removeChild(ele.children[0]);
            }
        });
    };

    private getPoints = () => this.props.pathParser?.getConnectionPoints();
}

export default Connected;
